// Raport z wizyty, nowy wygląd (handoff „design_handoff_pdf_documents”, 25.10.2026):
// - variant "simple" = v2 uproszczony (szablon MonitoringReportV3: monochrom, cienkie linie, status kropką)
// - variant "color"  = v2 (Design.pdf: ten sam układ z kolorowymi statusami, panelami i kafelkami)
// Dane, kolejność i wybór sekcji jak w DocumentGenerator (v1); zmienia się tylko prezentacja.
// Elementy wspólne z listami CL/FUL: ./pdfV2Kit.
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { Metadata } from "../../../../types/Metadata";
import { Answer, ParticipantAnswer, ProblemAnswer, ProblemType, RaportSection, TemplateType } from "../../../../types/Rows";
import { ActionStatus, ReportActionDto } from "../../../../types/reportActions";
import { isMultiDayVisit } from "../../../../utils/visitDate";
import { C, DesignVariant, StatusState, capitalizeWords, fmt, makeKit, pageStyle, weekday } from "./pdfV2Kit";

export type ReportDesignVariant = DesignVariant;

const answerLabel = (a: Answer) => {
  if (a.isBlank) return "";
  const v = (a.answer ?? "").toLowerCase();
  return v === "yes" ? "TAK" : v === "no" ? "NIE" : v === "na" ? "NA" : "";
};

const findingState = (status?: string): StatusState => {
  if (status === "new") return { label: "Nowe", open: true, tone: "red" };
  if (status === "ongoing") return { label: "Trwające", open: true, tone: "amber" };
  if (status === "finished") return { label: "Zakończone", open: false, tone: "green" };
  return { label: "Brak statusu", open: true, tone: "grey" };
};
const actionState = (s: ActionStatus): StatusState =>
  s === ActionStatus.Closed ? { label: "Zamknięte", open: false, tone: "green" } : { label: "Otwarte", open: true, tone: "amber" };

// Pytania z zależnościami: dziecko widoczne, gdy odpowiedź rodzica = requiredAnswerToBeVisible (jak w v1)
type QuestionRow = { num: string; depth: number; q: Answer };
const flattenQuestions = (section: RaportSection, sectionIndex: number): QuestionRow[] => {
  const all = section.questionsWithAnswer ?? [];
  const ids = new Set(all.map((q) => q.questionId));
  const byParent = new Map<string, Answer[]>();
  all.forEach((q) => {
    if (q.parentId && ids.has(q.parentId)) byParent.set(q.parentId, [...(byParent.get(q.parentId) ?? []), q]);
  });
  const sortW = (a: Answer, b: Answer) => (a.weight < b.weight ? -1 : a.weight > b.weight ? 1 : new Date(a.created) > new Date(b.created) ? 1 : -1);
  const rows: QuestionRow[] = [];
  const visit = (q: Answer, num: string, depth: number) => {
    rows.push({ num, depth, q });
    (byParent.get(q.questionId) ?? [])
      .filter((c) => (q.answer ?? "").toLowerCase() === (c.requiredAnswerToBeVisible ?? "").toLowerCase())
      .sort(sortW)
      .forEach((c, i) => visit(c, `${num}.${i + 1}`, depth + 1));
  };
  all
    .filter((q) => !(q.parentId && ids.has(q.parentId)) && q.requiredAnswerToBeVisible == null)
    .sort(sortW)
    .forEach((q, i) => visit(q, `${sectionIndex + 1}.${i + 1}`, 0));
  return rows;
};

export const ReportDocumentV2 = ({
  variant,
  reportName,
  visitLabel,
  resortName,
  address,
  metadata,
  problemAnswers,
  sections,
  participants,
  types,
  footer,
  author,
  composedDate,
  actions,
  categories = [],
}: {
  variant: ReportDesignVariant;
  reportName: string; // „Raport z wizyty monitorującej” (+ „ · IMV 4” w simple, „ w {ośrodek}” w color)
  visitLabel: string; // np. „IMV 4” (znaczek w color, stopka)
  resortName: string;
  address: string;
  metadata: Metadata;
  problemAnswers: ProblemAnswer[];
  sections: RaportSection[];
  participants: ParticipantAnswer[];
  actions: ReportActionDto[];
  types: TemplateType[];
  footer: string;
  author: string;
  composedDate: Date;
  categories?: { id: number; value: string }[];
}) => {
  const k = makeKit(variant);
  const { color, Muted, Pill, Status, SectionHeader, Table } = k;
  const has = (t: TemplateType) => types.includes(t);

  /* --- dane --- */
  const visitText = (() => {
    // Wizyta = nazwa raportu ("IMV 5"): typ i numer z nagłówka, jedno źródło numeru
    if (!metadata?.visitDate) return visitLabel || "—";
    const prefix = visitLabel || "";
    const date = isMultiDayVisit(metadata.visitDate, metadata.visitDateEnd)
      ? `${fmt(metadata.visitDate)} – ${fmt(metadata.visitDateEnd)}`
      : `${weekday(metadata.visitDate, color)}, ${fmt(metadata.visitDate)}`;
    return [prefix, date].filter(Boolean).join(" · ");
  })();
  const footerRight = color
    ? [metadata?.studyAcronym, metadata?.resortNumber, visitLabel].filter(Boolean).join(" · ")
    : [metadata?.protocolNumber, metadata?.resortNumber, visitLabel, "Poufne"].filter(Boolean).join(" · ");

  const serious = problemAnswers.filter((p) => p.problemType === ProblemType.MAJOR);
  const others = problemAnswers.filter((p) => p.problemType !== ProblemType.MAJOR);
  const questionGroups = [...sections]
    .sort((a, b) => (a.weight < b.weight ? -1 : 1))
    .map((s, i) => ({ title: `${i + 1}. ${s.name}`, rows: flattenQuestions(s, i) }));
  const allQuestions = questionGroups.flatMap((g) => g.rows).filter((r) => !r.q.isBlank);
  const countNo = allQuestions.filter((r) => answerLabel(r.q) === "NIE").length;
  const countNa = allQuestions.filter((r) => answerLabel(r.q) === "NA").length;
  const categoryName = (id?: number | null) => categories.find((c) => c.id === id)?.value?.trim() ?? "";

  /* --- sekcje specyficzne dla raportu --- */
  const Findings = () => {
    if (problemAnswers.length === 0) return null;
    let n = 0;
    const row = (p: ProblemAnswer) => {
      n += 1;
      return [
        <Muted>{String(n)}</Muted>,
        <View>
          <Text style={{ fontWeight: color ? 600 : 500 }}>{p.category}</Text>
          {!!p.description && <Muted>{p.description}</Muted>}
        </View>,
        <Status state={findingState(p.answer)} />,
        fmt(p.identificationDate),
        fmt(p.resolvedDate),
        p.personIdentifying || p.author || "—",
      ];
    };
    const groupRow = (label: string, red: boolean) => [
      <Text style={[{ fontSize: 8, fontWeight: 600, paddingTop: 4 }, color ? { textTransform: "uppercase", letterSpacing: 0.5, color: red ? C.redText : C.muted, fontSize: 7.5 } : {}]}>{label}</Text>,
      "", "", "", "", "",
    ];
    return (
      <View>
        <SectionHeader
          title="Znaleziska"
          right={color ? <Muted style={{ fontSize: 8 }}>{`${serious.length} poważne · ${others.length} pozostałe`}</Muted> : <k.StatusLegend />}
        />
        <Table
          columns={[
            { label: "#", width: "4%" },
            { label: "Znalezisko", width: "46%" },
            { label: "Status", width: "14%" },
            { label: "Zidentyfik.", width: "13%" },
            { label: "Rozwiązano", width: "13%" },
            { label: "Przez", width: "10%" },
          ]}
          rows={[
            ...(serious.length ? [groupRow("Poważne", true), ...serious.map(row)] : []),
            ...(others.length ? [groupRow("Pozostałe", false), ...others.map(row)] : []),
          ]}
        />
      </View>
    );
  };

  const Actions = () => {
    if (actions.length === 0) return null;
    const sorted = [...actions].sort((a, b) => (a.identificationDate || "").localeCompare(b.identificationDate || ""));
    const open = sorted.filter((a) => a.status !== ActionStatus.Closed).length;
    const flags = (a: ReportActionDto) =>
      [a.potentialDeviation && "PD", a.potentialSeriousFinding && "Poważne", a.seriousBreach && "Serious Breach"].filter(Boolean) as string[];
    return (
      <View>
        <SectionHeader
          title="Akcje otwarte i zamknięte"
          right={color ? <Muted style={{ fontSize: 8 }}>{`${open} otwarte · ${sorted.length - open} zamknięte`}</Muted> : undefined}
        />
        <Table
          columns={[
            { label: "Uczestnik", width: "12%" },
            { label: "Opis odstępstwa", width: "27%" },
            { label: "Status", width: "13%" },
            { label: "Identyf. / zamkn.", width: "12%" },
            { label: "Podjęte działania", width: "25%" },
            { label: "Pot. odstęp.", width: "11%" },
          ]}
          rows={sorted.map((a) => {
            const extra = [categoryName(a.categoryId), a.visitNumber].filter(Boolean).join(" · ");
            const f = flags(a);
            return [
              <Text style={{ fontWeight: color ? 600 : 500 }}>{a.participantNumber || "—"}</Text>,
              <View>
                <Text>{a.deviationDescription}</Text>
                {!!extra && <Muted style={{ fontSize: 7.5, marginTop: 1 }}>{extra}</Muted>}
              </View>,
              <Status state={actionState(a.status)} />,
              <View>
                <Text>{fmt(a.identificationDate)}</Text>
                <Text>{a.closedDate ? fmt(a.closedDate) : "—"}</Text>
              </View>,
              <View>
                <Text>{a.actionsTaken || "—"}</Text>
                {!!a.responsiblePerson && <Muted style={{ fontSize: 7.5, marginTop: 1 }}>{`Odpowiedzialny: ${a.responsiblePerson}`}</Muted>}
              </View>,
              color ? (
                <View>
                  <Pill label={a.potentialDeviation ? "TAK" : "NIE"} tone={a.potentialDeviation ? "red" : "grey"} />
                  {f.filter((x) => x !== "PD").map((x) => (
                    <Text key={x} style={{ fontSize: 7, color: C.redText, marginTop: 2 }}>{x}</Text>
                  ))}
                </View>
              ) : (
                <Text>{f.length ? f.join(", ") : "Nie"}</Text>
              ),
            ];
          })}
        />
      </View>
    );
  };

  const Questions = () => {
    const right = color ? (
      <Text style={{ fontSize: 8 }}>
        <Text style={{ color: C.muted }}>{`${allQuestions.length} pytań · `}</Text>
        <Text style={{ color: C.redText, fontWeight: 600 }}>{`${countNo} × NIE`}</Text>
        <Text style={{ color: C.muted }}>{` · ${countNa} × NA`}</Text>
      </Text>
    ) : (
      <Muted style={{ fontSize: 8 }}>{`${allQuestions.length} pytań · ${countNo} × NIE · ${countNa} × NA`}</Muted>
    );
    return (
      <View>
        <SectionHeader title="Pytania" right={right} />
        {questionGroups.map((g) => (
          <View key={g.title} style={{ marginBottom: 12 }}>
            <Text minPresenceAhead={30} style={{ fontSize: 9, fontWeight: 600, marginBottom: 2 }}>{g.title}</Text>
            {g.rows.map(({ num, depth, q }) => {
              const a = answerLabel(q);
              const no = a === "NIE";
              const comment = q.comment?.trim();
              return (
                <View
                  key={q.questionId}
                  wrap={false}
                  style={[
                    { flexDirection: "row", paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: C.row, paddingLeft: depth * 12 },
                    color && no ? { borderLeftWidth: 2, borderLeftColor: C.redText, paddingLeft: depth * 12 + 6 } : {},
                  ]}
                >
                  <Text style={[{ width: 28 + depth * 4, marginRight: 8, color: C.muted }, color && no ? { color: C.redText, fontWeight: 600 } : {}]}>{num}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={no ? { fontWeight: 600 } : {}}>{q.description}</Text>
                    {!!comment &&
                      (color ? (
                        <View style={{ backgroundColor: no ? C.redBg : C.panel, borderRadius: 3, paddingVertical: 4, paddingHorizontal: 8, marginTop: 4 }}>
                          <Text style={{ fontSize: 8 }}>
                            <Text style={{ fontWeight: 600 }}>{q.isDirectAnswer ? "Odpowiedź: " : "Komentarz: "}</Text>
                            {comment}
                          </Text>
                        </View>
                      ) : (
                        <Text style={{ fontSize: 8.5, color: C.muted, marginTop: 2 }}>
                          <Text style={{ color: C.text, fontWeight: 500 }}>{q.isDirectAnswer ? "Odpowiedź: " : "Komentarz: "}</Text>
                          {comment}
                        </Text>
                      ))}
                  </View>
                  <View style={{ width: 34, alignItems: "flex-end", marginLeft: 8 }}>
                    {!!a &&
                      (color ? (
                        a === "NA" ? (
                          <View style={{ borderWidth: 0.75, borderColor: C.sep, borderRadius: 6, paddingVertical: 1, paddingHorizontal: 5 }}>
                            <Text style={{ fontSize: 7, fontWeight: 600, color: C.muted }}>NA</Text>
                          </View>
                        ) : (
                          <Pill label={a} tone={no ? "red" : "green"} solid={no} />
                        )
                      ) : no ? (
                        <View style={{ backgroundColor: C.text, paddingVertical: 1, paddingHorizontal: 6 }}>
                          <Text style={{ fontSize: 8, fontWeight: 600, color: "#FFFFFF" }}>NIE</Text>
                        </View>
                      ) : (
                        <Text style={{ fontSize: 8, fontWeight: 500, color: C.muted }}>{a}</Text>
                      ))}
                  </View>
                </View>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        <k.PageHeader />

        <k.Title
          badge={`Raport ${visitLabel}`}
          note={`Sporządzono ${fmt(composedDate)}`}
          overline={`${reportName} · ${visitLabel}`}
          heading={color ? `${reportName} w ${resortName}` : resortName}
        />
        {has(TemplateType.METADATA) && (
          <k.MetaBlock
            items={[
              ["Numer protokołu", metadata?.protocolNumber || "—"],
              ["Akronim badania", metadata?.studyAcronym || "—"],
              ["Numer ośrodka", metadata?.resortNumber || "—"],
              ["Główny badacz", metadata?.mainResearcher || "—"],
              ["Wizyta", visitText],
              ["Adres", address || "—"],
            ]}
          />
        )}
        {has(TemplateType.STATUS) && (
          <View>
            <SectionHeader title="Uczestnicy wizyty" />
            <k.LabeledRows rows={[["CRO", metadata?.croParticipants], ["Ośrodek", metadata?.resortParticipants]]} />
          </View>
        )}
        {has(TemplateType.STATUS) && (
          <View>
            <SectionHeader title="Status ośrodka" />
            <k.SiteStatusBlock
              data={{
                recruitmentPct: metadata?.recrutationStatus ?? 0,
                randomized: metadata?.randomized ?? 0,
                declared: metadata?.declaredParticipants ?? 0,
                protocolDeviation: metadata?.protocolDeviation ?? 0,
                sae: metadata?.reportedSAE ?? 0,
                counts: [
                  ["Zadeklarowani", metadata?.declaredParticipants],
                  ["Skriningowani", metadata?.afterScreening],
                  ["Z niepowodzeniem skriningu", metadata?.screenFailure],
                  ["Zrandomizowani", metadata?.randomized],
                  ["W trakcie leczenia", metadata?.duringTreatment],
                  ["W trakcie obserwacji", metadata?.duringFollowUp],
                  ["Kończący przedwcześnie (EOT)", metadata?.prematureCompletion],
                  ["Zakończony udział (EOS)", metadata?.completedStudy],
                ],
              }}
            />
          </View>
        )}
        {has(TemplateType.ISSUES) && <Findings />}
        {has(TemplateType.PARTICIPANTS) && participants.length > 0 && (
          <View>
            <SectionHeader title="Zakres monitorowanych danych" />
            <k.MonitoredDataTable
              rows={participants.map((p) => ({
                participantNumber: p.participantNumber,
                visitNumber: p.visitNumber,
                sdv: !!p.isFinshed,
                rbv: !!p.isVerified,
                comment: p.comment,
              }))}
            />
          </View>
        )}
        {has(TemplateType.ACTIONS) && <Actions />}
        {has(TemplateType.QUESTIONS) && <Questions />}
        {has(TemplateType.FINALCOMMENT) && (
          <View>
            <SectionHeader title="Komentarz końcowy" />
            <Text style={{ fontSize: 9.5, lineHeight: 1.55, maxWidth: "75%" }}>{metadata?.finalComment || "—"}</Text>
          </View>
        )}

        <k.SignatureFields
          fields={[
            ["Data sporządzenia", fmt(composedDate)],
            [color ? "Imię i nazwisko monitora (CRA)" : "Monitor (CRA)", capitalizeWords(author)],
          ]}
        />
        <k.PageFooter left={has(TemplateType.FOOTER) ? footer : ""} right={footerRight} />
      </Page>
    </Document>
  );
};
